import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import { useTraining } from '../../context/TrainingContext';
import TranscriptViewer from '../../components/training/TranscriptViewer';
import DimensionScoreCard from '../../components/training/DimensionScoreCard';
import trainingApi from '../../services/trainingApi';
import { verdictChip, verdictColor } from '../../utils/trainingVerdict';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IE', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return '--';
  const mins = Math.floor(seconds / 60);
  return `${mins} min`;
};

const FeedbackPage = () => {
  const { id: sessionId } = useParams();
  const navigate = useNavigate();
  const { socket } = useTraining();

  const [session, setSession] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('evaluation');
  const [highlightedTurn, setHighlightedTurn] = useState(null);

  // Load session + evaluation
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [sessionRes, evalRes] = await Promise.all([
          trainingApi.getSession(sessionId),
          trainingApi.getEvaluation(sessionId).catch(() => null),
        ]);

        const _raw_sessionData = sessionRes.data || sessionRes; const sessionData = _raw_sessionData.data || _raw_sessionData;
        setSession(sessionData.session || sessionData);

        if (evalRes) {
          const _raw_evalData = evalRes.data || evalRes; const evalData = _raw_evalData.data || _raw_evalData;
          setEvaluation(evalData.evaluation || evalData);
        }
      } catch (err) {
        setError('Failed to load feedback data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [sessionId]);

  // Listen for evaluation completion via socket
  useEffect(() => {
    if (!socket) return;

    const handleEvaluationComplete = (data) => {
      if (data.sessionId === sessionId || data.evaluation) {
        setEvaluation(data.evaluation || data);
      }
    };

    socket.on('training:evaluation_complete', handleEvaluationComplete);
    return () => socket.off('training:evaluation_complete', handleEvaluationComplete);
  }, [socket, sessionId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Mode-aware routing + copy. Therapist-practice sessions return to the
  // Simulation Lab and use reflective-practice language rather than the
  // gradebook framing used for student/lecturer flows.
  const isTherapistPractice = session?.mode === 'therapist_practice';
  const returnTarget = isTherapistPractice ? '/therapist/simulation-lab' : '/training';
  const returnLabel = isTherapistPractice ? 'Return to Simulation Lab' : 'Return to Dashboard';
  // Supervisor-gated visibility on therapist_practice — when not released,
  // detail scoring is hidden by the backend's getEvaluation gate.
  const visibility = session?.feedbackVisibility || null;
  const aiReleased = !!session?.aiEvaluationReleased;
  const awaitingSupervisor = isTherapistPractice && visibility === 'supervisor_gated' && !aiReleased;
  const evaluationHidden = isTherapistPractice && visibility === 'never';

  if (error) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate(returnTarget)}>
          {returnLabel}
        </Button>
      </Box>
    );
  }

  const transcript = session?.transcript || [];
  const compositeScore = evaluation?.compositeScore;
  const maxCompositeScore = evaluation?.maxCompositeScore || 100;
  const passFail = evaluation?.passFail;
  const dimensions = evaluation?.dimensions || [];
  const criticalFlags = evaluation?.criticalFlags || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* Summary bar */}
      <Paper
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: evaluation ? `${verdictColor(passFail)}.light` : 'grey.100',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h6">{session?.scenarioName || 'Training Session'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(session?.completedAt || session?.startedAt)}
            {session?.durationSeconds ? ` | Duration: ${formatDuration(session.durationSeconds)}` : ''}
          </Typography>
        </Box>
        {evaluation && !evaluationHidden ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              fontWeight={700}
              color={`${verdictColor(passFail)}.dark`}
            >
              {compositeScore != null ? `${Math.round(compositeScore)}%` : '--'}
            </Typography>
            {passFail && (
              <Chip
                label={verdictChip(passFail, { therapistPractice: isTherapistPractice }).label}
                color={verdictChip(passFail, { therapistPractice: isTherapistPractice }).color}
                size="small"
              />
            )}
          </Box>
        ) : awaitingSupervisor ? (
          <Box sx={{ textAlign: 'center' }}>
            <Chip label="Awaiting supervisor review" color="info" />
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Your supervisor will release the AI feedback when ready.
            </Typography>
          </Box>
        ) : evaluationHidden ? (
          <Box sx={{ textAlign: 'center' }}>
            <Chip label="QA-only" />
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              AI scoring is captured for QA but not shown in this practice mode.
            </Typography>
          </Box>
        ) : session?.status === 'abandoned' ? (
          <Chip label="Cancelled" size="small" />
        ) : (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={32} sx={{ mb: 0.5 }} />
            <Typography variant="caption" display="block">Evaluation in progress...</Typography>
          </Box>
        )}
      </Paper>

      {/* Main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left panel: transcript */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, borderRight: 1, borderColor: 'divider' }}>
          <TranscriptViewer
            transcript={transcript}
            aiAnnotations={dimensions.flatMap(d => d.evidence || [])}
            highlightedTurn={highlightedTurn}
            onTurnHover={setHighlightedTurn}
          />
        </Box>

        {/* Right panel: evaluation details */}
        <Box sx={{ width: { md: 420, lg: 500 }, overflow: 'auto', p: 2, flexShrink: 0 }}>
          {!evaluation || evaluationHidden ? (
            session?.status === 'abandoned' ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Session was cancelled
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  No evaluation is available for cancelled sessions. You can review the transcript on the left.
                </Typography>
              </Box>
            ) : awaitingSupervisor ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  Awaiting supervisor review
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Your transcript is on the left. Detailed AI scoring will appear here once your supervisor releases it.
                </Typography>
              </Box>
            ) : evaluationHidden ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  AI scoring is QA-only in this practice mode
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Use your reflection and transcript to debrief. Detailed AI scoring isn't shown here by your supervisor's policy.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Evaluation in progress...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This may take a minute. The page will update automatically.
                </Typography>
              </Box>
            )
          ) : (
            <>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
                <Tab value="evaluation" label="AI Evaluation" />
                <Tab value="formulation" label="Formulation" />
              </Tabs>

              {activeTab === 'evaluation' && (
                <Box>
                  {/* Critical flags */}
                  {criticalFlags.length > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>Critical Flags</Typography>
                      {criticalFlags.map((flag, idx) => (
                        <Typography key={idx} variant="body2">
                          - {typeof flag === 'string' ? flag : `${flag.dimension || 'Unknown'}: ${flag.description || ''} (${flag.severity || ''})`}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  {/* Dimension scores */}
                  {dimensions.map((dim) => (
                    <DimensionScoreCard
                      key={dim.key || dim.name}
                      dimension={dim}
                      onEvidenceClick={(turnIndex) => setHighlightedTurn(turnIndex)}
                    />
                  ))}

                  {/* Strengths */}
                  {evaluation.strengthsSummary && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'success.main' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
                        Strengths
                      </Typography>
                      <Typography variant="body2">{evaluation.strengthsSummary}</Typography>
                    </Paper>
                  )}

                  {/* Areas for improvement */}
                  {evaluation.improvementSummary && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderColor: 'warning.main' }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
                        Areas for Improvement
                      </Typography>
                      <Typography variant="body2">{evaluation.improvementSummary}</Typography>
                    </Paper>
                  )}

                  {/* Recommended scenarios */}
                  {evaluation.recommendedScenarios?.length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        Recommended Next Scenarios
                      </Typography>
                      {evaluation.recommendedScenarios.map((rec, idx) => (
                        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                          - {rec.name || rec}
                        </Typography>
                      ))}
                    </Paper>
                  )}
                </Box>
              )}

              {activeTab === 'formulation' && (
                <Box>
                  {evaluation.formulationScore != null ? (
                    <>
                      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Formulation Score
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {evaluation.formulationScore.score}/{evaluation.formulationScore.maxScore}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(evaluation.formulationScore.score / evaluation.formulationScore.maxScore) * 100}
                          sx={{ mt: 1, height: 6, borderRadius: 3 }}
                        />
                      </Paper>
                      {evaluation.formulationScore.commentary && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            Feedback
                          </Typography>
                          <Typography variant="body2">
                            {evaluation.formulationScore.commentary}
                          </Typography>
                        </Paper>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      No formulation score available.
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Return to dashboard / lab */}
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => navigate(returnTarget)}>
              {returnLabel}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default FeedbackPage;
